import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import Navigation from "@/components/Navigation";
import Footer from "@/components/ui/footer";
import { 
  Heart, 
  Download, 
  Star, 
  Check, 
  ArrowRight, 
  Brain,
  Printer,
  Settings,
  Zap,
  Target,
  BookOpen,
  Headphones,
  Image,
  Share2,
  Lock,
  Globe,
  ListMusic,
  Mic2,
  Camera,
  Baby,
  UserPlus,
  Sparkles,
  Palette,
  Smartphone,
  Users
} from "lucide-react";
import { TeddyBear } from '@/components/icons/TeddyBear';
import { motion } from "framer-motion";

const mainFeatures = [
  {
    icon: Brain,
    title: "AI-Powered Story Generation",
    description: "Advanced artificial intelligence creates unique, personalized bedtime stories tailored to your child's age, interests, and learning goals.",
    details: [
      "Customizable characters and settings",
      "Age-appropriate content (2-12 years)",
      "Educational themes and moral lessons",
      "Interactive story elements",
      "Multiple story genres and styles"
    ],
    color: "from-purple-500 to-pink-500"
  },
  {
    icon: Image,
    title: "Character Studio (Premium Add-on)",
    description: "Transform family photos into magical cartoon characters that star in your personalized story illustrations - available as a premium subscription add-on.",
    details: [
      "Photo-to-cartoon AI transformation",
      "Available as $2.99/month or $29.99/year",
      "Consistent character design",
      "Family-friendly storybook style", 
      "Character gallery management"
    ],
    color: "from-rose-500 to-pink-500"
  },
  {
    icon: Sparkles,
    title: "AI Sticker Generation",
    description: "Create adorable custom stickers featuring your story characters - perfect for rewards, decorations, and making storytelling even more interactive.",
    details: [
      "Generate character stickers on demand",
      "High-quality PNG format (1024x1024)",
      "Features your custom cartoon characters",
      "Perfect for printing or digital use",
      "Add fun, interactive elements to stories"
    ],
    color: "from-yellow-500 to-orange-500"
  },
  {
    icon: Headphones,
    title: "Professional Narration",
    description: "High-quality text-to-speech with natural, engaging voices that bring stories to life with perfect pacing and emotion.",
    details: [
      "Multiple voice options",
      "Adjustable reading speed",
      "Natural pronunciation",
      "Emotional tone variation",
      "Background music integration"
    ],
    color: "from-blue-500 to-cyan-500"
  },
  {
    icon: Mic2,
    title: "Multi-Voice Character Narration",
    description: "Bring stories to life with different voices for each character - narrator, heroes, and villains each get their own unique voice.",
    details: [
      "Assign unique voices to characters",
      "Separate narrator voice",
      "Character dialogue detection",
      "Seamless voice transitions",
      "Enhanced storytelling experience"
    ],
    color: "from-indigo-500 to-purple-500"
  },
  {
    icon: Globe,
    title: "Multi-Language Support",
    description: "Create and enjoy stories in 10 languages - perfect for multilingual families and language learning.",
    details: [
      "10 languages supported",
      "English, Spanish, French, German, Chinese",
      "Japanese, Portuguese, Italian, Russian, Arabic",
      "Native narration voices",
      "Cultural adaptations"
    ],
    color: "from-emerald-500 to-teal-500"
  },
  {
    icon: ListMusic,
    title: "Story Playlists",
    description: "Create custom playlists of your favorite stories with auto-play mode for uninterrupted bedtime routines.",
    details: [
      "Create unlimited playlists",
      "Auto-play mode",
      "Share playlists publicly",
      "Organize stories by theme",
      "Perfect for bedtime routines"
    ],
    color: "from-violet-500 to-fuchsia-500"
  },
  {
    icon: Share2,
    title: "Community Story Sharing",
    description: "Share your favorite stories with the SnuggleCrafters community, discover top-rated stories from other families, and vote on your favorites.",
    details: [
      "Share stories publicly with one click",
      "Browse trending and top-rated stories",
      "Vote on community stories",
      "Discover creative story ideas",
      "Family-safe community moderation"
    ],
    color: "from-cyan-500 to-blue-500"
  },
  {
    icon: TeddyBear,
    title: "Multi-Child Profiles",
    description: "Create individual profiles for each child in your family with personalized settings, preferences, and story libraries.",
    details: [
      "First profile FREE with any plan",
      "Additional profiles $1.99 each (one-time)",
      "Save child details: name, age, birthday, gender, language",
      "Upload photo → AI generates cartoon avatar",
      "Character appears in story illustrations",
      "Separate story libraries per child",
      "Personalized recommendations"
    ],
    color: "from-pink-500 to-purple-500"
  },
  {
    icon: Image,
    title: "Personalized Illustrations",
    description: "AI-generated artwork featuring your custom characters from Character Studio in beautiful, story-matched scenes.",
    details: [
      "Your family as story characters",
      "High-resolution artwork",
      "Scene-by-scene illustrations",
      "Colorful and engaging visuals",
      "Print-ready quality"
    ],
    color: "from-green-500 to-emerald-500"
  },
  {
    icon: Download,
    title: "PDF Downloads & Printables",
    description: "Save stories as PDFs and create coloring books for offline reading, printing, and interactive activities.",
    details: [
      "High-quality PDF downloads",
      "Coloring book versions",
      "Printable character sheets",
      "Story activity pages",
      "Offline reading capability"
    ],
    color: "from-orange-500 to-red-500"
  },
  {
    icon: Printer,
    title: "Physical Book Printing",
    description: "Order professionally printed story books via Lulu print-on-demand with premium paper, binding, and cover options for lasting memories.",
    details: [
      "Professional Lulu print-on-demand",
      "Multiple book sizes available",
      "Premium paper and binding options",
      "Custom AI-generated cover designs",
      "Fast shipping worldwide",
      "Track orders directly in your account"
    ],
    color: "from-amber-500 to-yellow-500"
  },
  {
    icon: Smartphone,
    title: "Mobile Apps (iOS & Android)",
    description: "Take SnuggleCrafters anywhere with our native mobile apps - create and enjoy stories on the go with offline playback.",
    details: [
      "Native iOS and Android apps",
      "Offline story playback",
      "Download stories for travel",
      "Push notifications for new features",
      "Seamless sync across devices",
      "Optimized for tablets and phones"
    ],
    color: "from-sky-500 to-indigo-500"
  },
  {
    icon: Heart,
    title: "Personalized Content",
    description: "Stories tailored to your child's unique personality, interests, and developmental stage for maximum engagement.",
    details: [
      "Custom character names",
      "Personal interests integration",
      "Learning goal alignment",
      "Family member inclusion",
      "Cultural customization"
    ],
    color: "from-pink-500 to-rose-500"
  }
];

const additionalFeatures = [
  {
    icon: Settings,
    title: "Advanced Customization",
    description: "Fine-tune every aspect of your stories with detailed customization options.",
    features: [
      "Story length control",
      "Difficulty level adjustment",
      "Theme selection",
      "Character personality settings",
      "Educational focus areas"
    ]
  },
  {
    icon: Zap,
    title: "Instant Generation",
    description: "Create new stories in seconds with our lightning-fast AI processing.",
    features: [
      "Sub-30 second generation",
      "Real-time preview",
      "Instant editing",
      "Quick regeneration",
      "Batch story creation"
    ]
  },
  {
    icon: Target,
    title: "Educational Focus",
    description: "Stories designed to support your child's learning and development.",
    features: [
      "STEM concepts integration",
      "Social-emotional learning",
      "Language development",
      "Problem-solving skills",
      "Creativity enhancement"
    ]
  },
  {
    icon: BookOpen,
    title: "Story Library",
    description: "Access and manage your growing collection of personalized stories.",
    features: [
      "Unlimited story storage",
      "Easy organization",
      "Search and filter",
      "Favorites system",
      "Story sharing options"
    ]
  },
  {
    icon: Share2,
    title: "Family Sharing",
    description: "Share stories with family members and create collaborative experiences.",
    features: [
      "Multi-child profiles",
      "Family story sharing",
      "Grandparent access",
      "Collaborative editing",
      "Progress tracking"
    ]
  },
  {
    icon: Lock,
    title: "Privacy & Safety",
    description: "Your child's data and stories are protected with enterprise-grade security.",
    features: [
      "End-to-end encryption",
      "COPPA compliance",
      "No data selling",
      "Secure storage",
      "Parental controls"
    ]
  },
  {
    icon: Users,
    title: "Influencer Partner Program",
    description: "Join our partner program to earn commissions by sharing SnuggleCrafters with your audience.",
    features: [
      "Competitive referral commissions",
      "Stripe Connect automated payouts",
      "Real-time analytics dashboard",
      "Custom referral codes",
      "Marketing materials provided"
    ]
  }
];

const testimonials = [
  {
    name: "Sarah M.",
    role: "Mother of 2",
    content: "The AI creates such engaging stories that my kids ask for them every night. The personalization is incredible!",
    rating: 5
  },
  {
    name: "David L.",
    role: "Father",
    content: "The narration quality is amazing. It's like having a professional storyteller in our home every night.",
    rating: 5
  },
  {
    name: "Emily R.",
    role: "Teacher & Mom",
    content: "The educational value is incredible. My daughter has learned so much through these interactive stories.",
    rating: 5
  }
];


export default function FeaturesPage() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-teal-50">
      <Navigation />

      {/* Hero Section */}
      <section className="py-12 sm:py-20 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <Badge className="mb-4 bg-gradient-to-r from-purple-600 to-teal-600 text-white text-sm">
                ✨ Powerful Features
              </Badge>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold mb-4 sm:mb-6 bg-gradient-to-r from-purple-600 via-teal-600 to-amber-600 bg-clip-text text-transparent leading-tight">
                Everything You Need for Magical Storytelling
              </h1>
              <p className="text-lg sm:text-xl text-gray-700 mb-6 sm:mb-8 leading-relaxed max-w-4xl mx-auto">
                Discover the comprehensive suite of features that make <span className="text-snuggle-gradient">SnuggleCrafters.com</span> the ultimate 
                platform for creating personalized bedtime stories. Transform family photos into cartoon characters and watch your family 
                star in beautifully illustrated stories that spark imagination and create lasting memories.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link href="/auth">
                  <Button size="lg" variant="gradient" className="text-lg px-6 sm:px-8 py-4 sm:py-6 w-full sm:w-auto">
                    Start Your Free Trial
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <Link href="/pricing">
                  <Button size="lg" variant="outline" className="text-lg px-6 sm:px-8 py-4 sm:py-6 w-full sm:w-auto">
                    View Pricing
                  </Button>
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Main Features Section */}
      <section className="py-12 sm:py-20 px-4 bg-white">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4 bg-gradient-to-r from-purple-600 to-teal-600 bg-clip-text text-transparent">
              Core Features
            </h2>
            <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto">
              Our main features work together to create the perfect storytelling experience for your family
            </p>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {mainFeatures.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 50 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                >
                  <Card className="h-full hover:shadow-lg transition-shadow duration-300">
                    <CardHeader>
                      <div className={`w-12 h-12 bg-gradient-to-r ${feature.color} rounded-lg flex items-center justify-center mb-4`}>
                        <Icon className="h-6 w-6 text-white" />
                      </div>
                      <CardTitle className="text-xl">{feature.title}</CardTitle>
                      <CardDescription className="text-base">
                        {feature.description}
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-2">
                        {feature.details.map((detail, detailIndex) => (
                          <li key={detailIndex} className="flex items-center gap-2 text-sm text-gray-600">
                            <Check className="h-4 w-4 text-green-600 flex-shrink-0" />
                            <span>{detail}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Child Profile Upload vs Character Studio Comparison Section */}
      <section className="py-12 sm:py-20 px-4 bg-gradient-to-br from-rose-50 via-purple-50 to-pink-50">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4 bg-gradient-to-r from-rose-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">
              Two Ways to Create Characters: Which is Right for You?
            </h2>
            <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto">
              Choose the character creation method that best fits your needs and budget
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 relative">
            {/* Child Profile Photo Upload Card */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              data-testid="comparison-child-profile"
            >
              <Card className="h-full bg-gradient-to-br from-rose-50 to-pink-50 border-2 border-rose-200 hover:shadow-xl transition-all duration-300">
                <CardHeader>
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-14 h-14 bg-gradient-to-r from-rose-500 to-pink-500 rounded-lg flex items-center justify-center shadow-lg">
                      <Camera className="h-7 w-7 text-white" />
                    </div>
                    <Badge className="bg-gradient-to-r from-green-500 to-emerald-500 text-white text-sm px-4 py-1 shadow-md">
                      INCLUDED FREE
                    </Badge>
                  </div>
                  <CardTitle className="text-2xl text-rose-900">
                    Child Profile Photo Upload
                  </CardTitle>
                  <CardDescription className="text-base text-rose-700 mt-3">
                    Upload your child's photo when creating their profile - AI auto-generates a cartoon avatar character
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3 text-sm text-gray-700">
                      <Check className="h-5 w-5 text-rose-600 flex-shrink-0 mt-0.5" />
                      <span>Part of child profile system</span>
                    </li>
                    <li className="flex items-start gap-3 text-sm text-gray-700">
                      <Check className="h-5 w-5 text-rose-600 flex-shrink-0 mt-0.5" />
                      <span>Upload photo during profile creation</span>
                    </li>
                    <li className="flex items-start gap-3 text-sm text-gray-700">
                      <Check className="h-5 w-5 text-rose-600 flex-shrink-0 mt-0.5" />
                      <span>One character per profile</span>
                    </li>
                    <li className="flex items-start gap-3 text-sm text-gray-700">
                      <Check className="h-5 w-5 text-rose-600 flex-shrink-0 mt-0.5" />
                      <span>Auto-fills story wizard with child's info</span>
                    </li>
                    <li className="flex items-start gap-3 text-sm text-gray-700">
                      <Check className="h-5 w-5 text-rose-600 flex-shrink-0 mt-0.5" />
                      <span>Character appears in all story illustrations</span>
                    </li>
                    <li className="flex items-start gap-3 text-sm text-gray-700">
                      <Check className="h-5 w-5 text-rose-600 flex-shrink-0 mt-0.5" />
                      <span>First profile FREE, additional $1.99/profile</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </motion.div>

            {/* VS Badge - centered between cards */}
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10 hidden lg:block">
              <div className="w-16 h-16 bg-gradient-to-br from-purple-600 to-pink-600 rounded-full flex items-center justify-center shadow-xl border-4 border-white">
                <span className="text-white font-bold text-lg">VS</span>
              </div>
            </div>

            {/* VS Badge for mobile - appears between stacked cards */}
            <div className="flex justify-center lg:hidden -my-4 z-10 relative">
              <div className="w-16 h-16 bg-gradient-to-br from-purple-600 to-pink-600 rounded-full flex items-center justify-center shadow-xl border-4 border-white">
                <span className="text-white font-bold text-lg">VS</span>
              </div>
            </div>

            {/* Character Studio Card */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              data-testid="comparison-character-studio"
            >
              <Card className="h-full bg-gradient-to-br from-purple-50 to-violet-50 border-2 border-purple-200 hover:shadow-xl transition-all duration-300">
                <CardHeader>
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-14 h-14 bg-gradient-to-r from-purple-600 to-violet-600 rounded-lg flex items-center justify-center shadow-lg">
                      <Palette className="h-7 w-7 text-white" />
                    </div>
                    <Badge className="bg-gradient-to-r from-purple-600 to-violet-600 text-white text-sm px-4 py-1 shadow-md">
                      $2.99/month or $29.99/year
                    </Badge>
                  </div>
                  <CardTitle className="text-2xl text-purple-900">
                    Character Studio
                  </CardTitle>
                  <CardDescription className="text-base text-purple-700 mt-3">
                    Premium subscription add-on for advanced character customization and multiple character creation
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3 text-sm text-gray-700">
                      <Check className="h-5 w-5 text-purple-600 flex-shrink-0 mt-0.5" />
                      <span>Separate premium add-on service</span>
                    </li>
                    <li className="flex items-start gap-3 text-sm text-gray-700">
                      <Check className="h-5 w-5 text-purple-600 flex-shrink-0 mt-0.5" />
                      <span>Create unlimited characters</span>
                    </li>
                    <li className="flex items-start gap-3 text-sm text-gray-700">
                      <Check className="h-5 w-5 text-purple-600 flex-shrink-0 mt-0.5" />
                      <span>Advanced AI customization options</span>
                    </li>
                    <li className="flex items-start gap-3 text-sm text-gray-700">
                      <Check className="h-5 w-5 text-purple-600 flex-shrink-0 mt-0.5" />
                      <span>Character trait customization</span>
                    </li>
                    <li className="flex items-start gap-3 text-sm text-gray-700">
                      <Check className="h-5 w-5 text-purple-600 flex-shrink-0 mt-0.5" />
                      <span>Independent of child profiles</span>
                    </li>
                    <li className="flex items-start gap-3 text-sm text-gray-700">
                      <Check className="h-5 w-5 text-purple-600 flex-shrink-0 mt-0.5" />
                      <span>Family member characters possible</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </motion.div>
          </div>

          <div className="text-center mt-12">
            <p className="text-gray-600 text-lg">
              <strong>Quick Tip:</strong> Start with the free child profile photo upload, then upgrade to Character Studio later if you want more customization options
            </p>
          </div>
        </div>
      </section>

      {/* Story Flow Timeline Section */}
      <section className="py-12 sm:py-20 px-4 bg-white">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4 bg-gradient-to-r from-purple-600 to-teal-600 bg-clip-text text-transparent">
              From Profile to Story: How It All Works Together
            </h2>
            <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto">
              Follow the simple journey from creating your child's profile to enjoying personalized bedtime stories
            </p>
          </div>

          {/* Desktop horizontal timeline */}
          <div className="hidden lg:block">
            <div className="relative">
              {/* Connection line */}
              <div className="absolute top-16 left-0 right-0 h-1 bg-gradient-to-r from-purple-200 via-teal-200 to-purple-200" />
              
              <div className="grid grid-cols-5 gap-4 relative z-10">
                {/* Step 1: Create Child Profile */}
                <motion.div
                  initial={{ opacity: 0, y: 50 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.1 }}
                  className="flex flex-col items-center"
                  data-testid="timeline-step-1"
                >
                  <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-purple-600 rounded-full flex items-center justify-center mb-4 shadow-lg border-4 border-white relative">
                    <UserPlus className="h-8 w-8 text-white" />
                    <div className="absolute -top-2 -right-2 w-8 h-8 bg-white rounded-full flex items-center justify-center shadow-md border-2 border-purple-500">
                      <span className="text-purple-600 font-bold text-sm">1</span>
                    </div>
                  </div>
                  <h3 className="font-bold text-lg text-gray-900 mb-2 text-center">Create Child Profile</h3>
                  <p className="text-sm text-gray-600 text-center mb-2">Save child's details and preferences</p>
                  <p className="text-xs text-gray-500 text-center">Name, age, birthday, gender, language</p>
                </motion.div>

                {/* Step 2: Upload Photo */}
                <motion.div
                  initial={{ opacity: 0, y: 50 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                  className="flex flex-col items-center"
                  data-testid="timeline-step-2"
                >
                  <div className="w-16 h-16 bg-gradient-to-br from-rose-500 to-pink-600 rounded-full flex items-center justify-center mb-4 shadow-lg border-4 border-white relative">
                    <Camera className="h-8 w-8 text-white" />
                    <div className="absolute -top-2 -right-2 w-8 h-8 bg-white rounded-full flex items-center justify-center shadow-md border-2 border-rose-500">
                      <span className="text-rose-600 font-bold text-sm">2</span>
                    </div>
                  </div>
                  <h3 className="font-bold text-lg text-gray-900 mb-2 text-center">Upload Photo</h3>
                  <p className="text-sm text-gray-600 text-center mb-2">Add child's photo for avatar generation</p>
                  <p className="text-xs text-gray-500 text-center">AI creates cartoon character automatically</p>
                </motion.div>

                {/* Step 3: Generate Story */}
                <motion.div
                  initial={{ opacity: 0, y: 50 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.3 }}
                  className="flex flex-col items-center"
                  data-testid="timeline-step-3"
                >
                  <div className="w-16 h-16 bg-gradient-to-br from-amber-500 to-yellow-600 rounded-full flex items-center justify-center mb-4 shadow-lg border-4 border-white relative">
                    <Sparkles className="h-8 w-8 text-white" />
                    <div className="absolute -top-2 -right-2 w-8 h-8 bg-white rounded-full flex items-center justify-center shadow-md border-2 border-amber-500">
                      <span className="text-amber-600 font-bold text-sm">3</span>
                    </div>
                  </div>
                  <h3 className="font-bold text-lg text-gray-900 mb-2 text-center">Generate Story</h3>
                  <p className="text-sm text-gray-600 text-center mb-2">Story wizard auto-fills with profile data</p>
                  <p className="text-xs text-gray-500 text-center">Child's info pre-populated</p>
                </motion.div>

                {/* Step 4: Character in Illustrations */}
                <motion.div
                  initial={{ opacity: 0, y: 50 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                  className="flex flex-col items-center"
                  data-testid="timeline-step-4"
                >
                  <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-full flex items-center justify-center mb-4 shadow-lg border-4 border-white relative">
                    <Image className="h-8 w-8 text-white" />
                    <div className="absolute -top-2 -right-2 w-8 h-8 bg-white rounded-full flex items-center justify-center shadow-md border-2 border-teal-500">
                      <span className="text-teal-600 font-bold text-sm">4</span>
                    </div>
                  </div>
                  <h3 className="font-bold text-lg text-gray-900 mb-2 text-center">Character in Illustrations</h3>
                  <p className="text-sm text-gray-600 text-center mb-2">Avatar appears in story artwork</p>
                  <p className="text-xs text-gray-500 text-center">Personalized visual experience</p>
                </motion.div>

                {/* Step 5: Listen & Share */}
                <motion.div
                  initial={{ opacity: 0, y: 50 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.5 }}
                  className="flex flex-col items-center"
                  data-testid="timeline-step-5"
                >
                  <div className="w-16 h-16 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-full flex items-center justify-center mb-4 shadow-lg border-4 border-white relative">
                    <Headphones className="h-8 w-8 text-white" />
                    <div className="absolute -top-2 -right-2 w-8 h-8 bg-white rounded-full flex items-center justify-center shadow-md border-2 border-indigo-500">
                      <span className="text-indigo-600 font-bold text-sm">5</span>
                    </div>
                  </div>
                  <h3 className="font-bold text-lg text-gray-900 mb-2 text-center">Listen & Share</h3>
                  <p className="text-sm text-gray-600 text-center mb-2">Enjoy story with narration</p>
                  <p className="text-xs text-gray-500 text-center">Save to library, create playlists</p>
                </motion.div>
              </div>
            </div>
          </div>

          {/* Mobile vertical timeline */}
          <div className="lg:hidden space-y-8">
            {/* Step 1: Create Child Profile */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="flex gap-4"
              data-testid="timeline-step-1-mobile"
            >
              <div className="flex flex-col items-center">
                <div className="w-14 h-14 bg-gradient-to-br from-purple-500 to-purple-600 rounded-full flex items-center justify-center shadow-lg border-4 border-white relative flex-shrink-0">
                  <UserPlus className="h-6 w-6 text-white" />
                  <div className="absolute -top-1 -right-1 w-6 h-6 bg-white rounded-full flex items-center justify-center shadow-md border-2 border-purple-500">
                    <span className="text-purple-600 font-bold text-xs">1</span>
                  </div>
                </div>
                <div className="w-0.5 h-full bg-gradient-to-b from-purple-200 to-rose-200 mt-2" />
              </div>
              <div className="pb-8">
                <h3 className="font-bold text-lg text-gray-900 mb-1">Create Child Profile</h3>
                <p className="text-sm text-gray-600 mb-1">Save child's details and preferences</p>
                <p className="text-xs text-gray-500">Name, age, birthday, gender, language</p>
              </div>
            </motion.div>

            {/* Step 2: Upload Photo */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="flex gap-4"
              data-testid="timeline-step-2-mobile"
            >
              <div className="flex flex-col items-center">
                <div className="w-14 h-14 bg-gradient-to-br from-rose-500 to-pink-600 rounded-full flex items-center justify-center shadow-lg border-4 border-white relative flex-shrink-0">
                  <Camera className="h-6 w-6 text-white" />
                  <div className="absolute -top-1 -right-1 w-6 h-6 bg-white rounded-full flex items-center justify-center shadow-md border-2 border-rose-500">
                    <span className="text-rose-600 font-bold text-xs">2</span>
                  </div>
                </div>
                <div className="w-0.5 h-full bg-gradient-to-b from-rose-200 to-amber-200 mt-2" />
              </div>
              <div className="pb-8">
                <h3 className="font-bold text-lg text-gray-900 mb-1">Upload Photo</h3>
                <p className="text-sm text-gray-600 mb-1">Add child's photo for avatar generation</p>
                <p className="text-xs text-gray-500">AI creates cartoon character automatically</p>
              </div>
            </motion.div>

            {/* Step 3: Generate Story */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="flex gap-4"
              data-testid="timeline-step-3-mobile"
            >
              <div className="flex flex-col items-center">
                <div className="w-14 h-14 bg-gradient-to-br from-amber-500 to-yellow-600 rounded-full flex items-center justify-center shadow-lg border-4 border-white relative flex-shrink-0">
                  <Sparkles className="h-6 w-6 text-white" />
                  <div className="absolute -top-1 -right-1 w-6 h-6 bg-white rounded-full flex items-center justify-center shadow-md border-2 border-amber-500">
                    <span className="text-amber-600 font-bold text-xs">3</span>
                  </div>
                </div>
                <div className="w-0.5 h-full bg-gradient-to-b from-amber-200 to-teal-200 mt-2" />
              </div>
              <div className="pb-8">
                <h3 className="font-bold text-lg text-gray-900 mb-1">Generate Story</h3>
                <p className="text-sm text-gray-600 mb-1">Story wizard auto-fills with profile data</p>
                <p className="text-xs text-gray-500">Child's info pre-populated</p>
              </div>
            </motion.div>

            {/* Step 4: Character in Illustrations */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="flex gap-4"
              data-testid="timeline-step-4-mobile"
            >
              <div className="flex flex-col items-center">
                <div className="w-14 h-14 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-full flex items-center justify-center shadow-lg border-4 border-white relative flex-shrink-0">
                  <Image className="h-6 w-6 text-white" />
                  <div className="absolute -top-1 -right-1 w-6 h-6 bg-white rounded-full flex items-center justify-center shadow-md border-2 border-teal-500">
                    <span className="text-teal-600 font-bold text-xs">4</span>
                  </div>
                </div>
                <div className="w-0.5 h-full bg-gradient-to-b from-teal-200 to-indigo-200 mt-2" />
              </div>
              <div className="pb-8">
                <h3 className="font-bold text-lg text-gray-900 mb-1">Character in Illustrations</h3>
                <p className="text-sm text-gray-600 mb-1">Avatar appears in story artwork</p>
                <p className="text-xs text-gray-500">Personalized visual experience</p>
              </div>
            </motion.div>

            {/* Step 5: Listen & Share */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="flex gap-4"
              data-testid="timeline-step-5-mobile"
            >
              <div className="flex flex-col items-center">
                <div className="w-14 h-14 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-full flex items-center justify-center shadow-lg border-4 border-white relative flex-shrink-0">
                  <Headphones className="h-6 w-6 text-white" />
                  <div className="absolute -top-1 -right-1 w-6 h-6 bg-white rounded-full flex items-center justify-center shadow-md border-2 border-indigo-500">
                    <span className="text-indigo-600 font-bold text-xs">5</span>
                  </div>
                </div>
              </div>
              <div>
                <h3 className="font-bold text-lg text-gray-900 mb-1">Listen & Share</h3>
                <p className="text-sm text-gray-600 mb-1">Enjoy story with narration</p>
                <p className="text-xs text-gray-500">Save to library, create playlists</p>
              </div>
            </motion.div>
          </div>

          <div className="text-center mt-12">
            <Link href="/auth">
              <Button size="lg" variant="gradient" className="text-lg px-8 py-4 shadow-lg hover:shadow-xl transition-shadow" data-testid="timeline-cta-button">
                <Sparkles className="mr-2 h-5 w-5" />
                Start Creating Stories
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Additional Features Section */}
      <section className="py-12 sm:py-20 px-4 bg-gradient-to-br from-purple-50 to-teal-50">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4 bg-gradient-to-r from-purple-600 to-teal-600 bg-clip-text text-transparent">
              Additional Features
            </h2>
            <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto">
              Even more features to enhance your storytelling experience
            </p>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {additionalFeatures.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 50 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                >
                  <Card className="h-full hover:shadow-lg transition-shadow duration-300">
                    <CardHeader>
                      <div className="w-12 h-12 bg-gradient-to-r from-gray-600 to-gray-800 rounded-lg flex items-center justify-center mb-4">
                        <Icon className="h-6 w-6 text-white" />
                      </div>
                      <CardTitle className="text-xl">{feature.title}</CardTitle>
                      <CardDescription className="text-base">
                        {feature.description}
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-2">
                        {feature.features.map((item, itemIndex) => (
                          <li key={itemIndex} className="flex items-center gap-2 text-sm text-gray-600">
                            <Check className="h-4 w-4 text-green-600 flex-shrink-0" />
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Verified Community Section */}
      <section className="py-12 sm:py-20 px-4 bg-gradient-to-br from-amber-50 to-yellow-50">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4 bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text text-transparent">
              Verified Community Members
            </h2>
            <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto">
              Join our exclusive community of verified Official Bear Insiders who help shape the future of personalized storytelling
            </p>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              <Card className="h-full bg-gradient-to-br from-amber-100 to-yellow-100 border-2 border-amber-200 hover:shadow-lg transition-shadow duration-300">
                <CardHeader className="text-center">
                  <div className="w-16 h-16 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                    <span className="text-2xl">🍯</span>
                  </div>
                  <CardTitle className="text-xl text-amber-800 flex items-center justify-center gap-2">
                    Official Bear Insider
                    <Badge className="bg-gradient-to-r from-amber-400 to-orange-500 text-white border-0">
                      Verified
                    </Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-amber-700 text-center">
                    <ul className="space-y-2 text-sm">
                      <li className="flex items-center gap-2">
                        <Check className="h-4 w-4 text-amber-600" />
                        <span>Exclusive honeypot verification shield</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <Check className="h-4 w-4 text-amber-600" />
                        <span>Early access to new features</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <Check className="h-4 w-4 text-amber-600" />
                        <span>Direct influence on app development</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <Check className="h-4 w-4 text-amber-600" />
                        <span>Priority customer support</span>
                      </li>
                    </ul>
                  </CardDescription>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <Card className="h-full bg-gradient-to-br from-purple-100 to-pink-100 border-2 border-purple-200 hover:shadow-lg transition-shadow duration-300">
                <CardHeader className="text-center">
                  <div className="w-16 h-16 bg-gradient-to-br from-purple-400 to-pink-500 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                    <Star className="h-8 w-8 text-white" />
                  </div>
                  <CardTitle className="text-xl text-purple-800 flex items-center justify-center gap-2">
                    App Founder
                    <Badge className="bg-gradient-to-r from-purple-400 to-pink-500 text-white border-0">
                      Exclusive
                    </Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-purple-700 text-center">
                    <ul className="space-y-2 text-sm">
                      <li className="flex items-center gap-2">
                        <Check className="h-4 w-4 text-purple-600" />
                        <span>Founder crown verification badge</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <Check className="h-4 w-4 text-purple-600" />
                        <span>All premium features included</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <Check className="h-4 w-4 text-purple-600" />
                        <span>Lifetime access to the platform</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <Check className="h-4 w-4 text-purple-600" />
                        <span>Special recognition in community</span>
                      </li>
                    </ul>
                  </CardDescription>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <Card className="h-full bg-gradient-to-br from-teal-100 to-cyan-100 border-2 border-teal-200 hover:shadow-lg transition-shadow duration-300">
                <CardHeader className="text-center">
                  <div className="w-16 h-16 bg-gradient-to-br from-teal-400 to-cyan-500 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                    <Heart className="h-8 w-8 text-white" />
                  </div>
                  <CardTitle className="text-xl text-teal-800 flex items-center justify-center gap-2">
                    Community Member
                    <Badge className="bg-gradient-to-r from-teal-400 to-cyan-500 text-white border-0">
                      Active
                    </Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-teal-700 text-center">
                    <ul className="space-y-2 text-sm">
                      <li className="flex items-center gap-2">
                        <Check className="h-4 w-4 text-teal-600" />
                        <span>Share stories with the community</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <Check className="h-4 w-4 text-teal-600" />
                        <span>Vote on favorite stories</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <Check className="h-4 w-4 text-teal-600" />
                        <span>Connect with other families</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <Check className="h-4 w-4 text-teal-600" />
                        <span>Discover new story ideas</span>
                      </li>
                    </ul>
                  </CardDescription>
                </CardContent>
              </Card>
            </motion.div>
          </div>

          <div className="text-center mt-12">
            <p className="text-gray-600 mb-6">
              Verification badges help you identify trusted community members and showcase your status
            </p>
            <Link href="/community">
              <Button size="lg" variant="gradient" className="text-lg px-8 py-4 shadow-lg hover:shadow-xl transition-shadow">
                <Heart className="mr-2 h-5 w-5" />
                Join Our Community
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-12 sm:py-20 px-4 bg-white">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4 bg-gradient-to-r from-purple-600 to-teal-600 bg-clip-text text-transparent">
              What Parents Are Saying
            </h2>
            <p className="text-lg sm:text-xl text-gray-600">
              Join thousands of families who love our features
            </p>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <Card className="h-full">
                  <CardContent className="pt-6">
                    <div className="flex items-center mb-4">
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <Star key={i} className="h-5 w-5 text-yellow-400 fill-current" />
                      ))}
                    </div>
                    <p className="text-gray-700 mb-4 italic">"{testimonial.content}"</p>
                    <div>
                      <p className="font-semibold text-gray-900">{testimonial.name}</p>
                      <p className="text-sm text-gray-600">{testimonial.role}</p>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 sm:py-20 px-4 bg-gradient-to-r from-purple-600 to-teal-600">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Ready to Experience These Features?
          </h2>
          <p className="text-lg sm:text-xl text-purple-100 mb-6 sm:mb-8">
            Start your free trial today and discover all the amazing features <span className="text-snuggle-gradient">SnuggleCrafters.com</span> has to offer
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link href="/auth">
              <Button size="lg" variant="secondary" className="text-lg px-6 sm:px-8 py-4 sm:py-6 w-full sm:w-auto">
                Start Your Free Trial
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link href="/pricing">
              <Button size="lg" variant="outline" className="text-lg px-6 sm:px-8 py-4 sm:py-6 border-white text-white hover:bg-white hover:text-purple-600 w-full sm:w-auto">
                View Pricing
              </Button>
            </Link>
          </div>
          <p className="text-purple-200 mt-4 sm:mt-6 text-sm">
            No credit card required • 7-day free trial • Cancel anytime
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
}